import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

export type NotificationType = 'success' | 'error' | 'warning' | 'info';

export interface Notification {
  id: string;
  message: string;
  title?: string;
  type: NotificationType;
  timestamp: number;
  expiresAt: number;
  read: boolean;
  meta?: any; // optional payload (e.g. { downloadUrl })
  correlationId?: string;
}

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  addNotification: (message: string, type?: NotificationType, title?: string, expirationMinutes?: number) => void;
  removeNotification: (id: string) => void;
  markAsRead: (id: string) => void;
  clearAll: () => void;
}

const NotificationContext = createContext<NotificationContextType | null>(null);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};

// --- Cookie Utilities ---
const setCookie = (name: string, value: string, minutes: number = 1440) => {
  const date = new Date();
  date.setTime(date.getTime() + minutes * 60 * 1000);
  const expires = `expires=${date.toUTCString()}`;
  document.cookie = `${name}=${encodeURIComponent(value)};${expires};path=/`;
};

const getCookie = (name: string): string | null => {
  const nameEQ = `${name}=`;
  const cookies = document.cookie.split(';');
  for (let cookie of cookies) {
    cookie = cookie.trim();
    if (cookie.indexOf(nameEQ) === 0) {
      return decodeURIComponent(cookie.substring(nameEQ.length));
    }
  }
  return null;
};

const deleteCookie = (name: string) => {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/`;
};

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);

  // Load notifications from cookies on mount
  useEffect(() => {
    const loadFromCookies = () => {
      const storedNotifications = getCookie('app_notifications');
      if (storedNotifications) {
        try {
          const parsed = JSON.parse(storedNotifications) as Notification[];
          // Filter out expired notifications
          const active = parsed.filter((n) => n.expiresAt > Date.now());
          setNotifications(active.slice(-30)); // Keep only last 30
          // Update cookie with non-expired items
          if (active.length > 0) {
            setCookie('app_notifications', JSON.stringify(active.slice(-30)), 1440);
          }
        } catch (error) {
          console.error('Error parsing notifications from cookie:', error);
        }
      }
    };

    loadFromCookies();
  }, []);

  // Persist notifications to cookies whenever they change
  useEffect(() => {
    if (notifications.length > 0) {
      const toStore = notifications.slice(-30); // Keep only last 30
      setCookie('app_notifications', JSON.stringify(toStore), 1440);
    }
  }, [notifications]);

  // Listen for toast save events
  useEffect(() => {
    const handleSaveToNotification = (event: Event) => {
      const customEvent = event as CustomEvent;
      const { message, title, type, expirationMinutes, meta, correlationId } = customEvent.detail;
      addNotification(message, type, title, expirationMinutes, meta, correlationId);
    };

    window.addEventListener('saveToNotification', handleSaveToNotification);
    return () => {
      window.removeEventListener('saveToNotification', handleSaveToNotification);
    };
  }, []);

  const addNotification = useCallback(
    (
      message: string,
      type: NotificationType = 'info',
      title?: string,
      expirationMinutes: number = 30,
      meta?: any,
      correlationId?: string,
    ) => {
      const id = `notif_${Date.now()}_${Math.random()}`;
      const now = Date.now();

      const newNotification: Notification = {
        id,
        message,
        title,
        type,
        timestamp: now,
        expiresAt: now + expirationMinutes * 60 * 1000,
        read: false,
        meta,
        correlationId,
      };

      setNotifications((prev) => {
        const updated = [...prev, newNotification];
        // Keep only last 30
        return updated.slice(-30);
      });
    },
    [],
  );

  const removeNotification = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((n) => n.id !== id));
  }, []);

  const markAsRead = useCallback((id: string) => {
    setNotifications((prev) => prev.map((n) => (n.id === id ? { ...n, read: true } : n)));
  }, []);

  const clearAll = useCallback(() => {
    setNotifications([]);
    deleteCookie('app_notifications');
  }, []);

  const unreadCount = notifications.filter((n) => !n.read).length;

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        addNotification,
        removeNotification,
        markAsRead,
        clearAll,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};
